/**
 * What the fleet could move to, every plan, and the location names, shared by the updates
 * dots, the server updates and the server surfaces so they cannot disagree.
 *
 * Releases are weekly and nothing pushes a new one, so an hourly reload covers a tab left
 * open; a plan changed anywhere arrives as an `update_plans` change frame. Each server's own
 * update report lives in serversAtoms, fetched apart from the list because nothing pushes it.
 * The location names come from locationsAtoms, which every other locations reader shares.
 */

import { atom } from 'jotai';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { HOUR_MS } from '@/lib/timeUnits';
import { NO_UPDATES } from '@/lib/updatesViewUtils';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import type { AvailableUpdates } from '@/types/servers';
import type { UpdatesSnapshot } from '@/types/updates';

import { locationsAtom } from './locationsAtoms';
import { memberPermissionsAtom } from './memberPermissionsAtoms';

export const UPDATES_POLL_MS = HOUR_MS;

type Fetched = Omit<UpdatesSnapshot, 'locations'>;

const EMPTY: Fetched = {
    availableUpdates: null,
    plans: [],
    isLoaded: false,
    isLoading: false,
    error: null,
};

const snapshot = snapshotAtoms<Fetched>({ ...EMPTY, isLoading: true }, async () => {
    const [availableValue, plans] = await Promise.all([
        api.getAvailableUpdates(),
        api.listUpdatePlans(),
    ]);
    return {
        availableUpdates: availableValue,
        plans,
        isLoaded: true,
        isLoading: false,
        error: null,
    };
}, { refreshEveryMs: UPDATES_POLL_MS });

// The names only label the plan groups, so the locations' own load state stays out of it.
export const updatesAtom = atom((get): UpdatesSnapshot => ({
    ...get(snapshot.value),
    locations: get(locationsAtom).locations,
}));
// Only an admin manages the updates, so a viewer's shell never loads them.
export const adminAvailableUpdatesAtom = atom((get): AvailableUpdates => {
    if (!get(memberPermissionsAtom).canManage) {
        return NO_UPDATES;
    }
    return get(snapshot.value).availableUpdates ?? NO_UPDATES;
});
export const refreshUpdatesAtom = snapshot.reload;
export const revalidateUpdatesAtom = snapshot.revalidate;

/** What the notifications stream means for updates. */
export const updatesStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    const hasPlanChanged = input.kind === StreamInputKind.Frame &&
        input.frame.type === 'change' && input.frame.resource === 'update_plans';
    if (hasPlanChanged || input.kind === StreamInputKind.Reopened) {
        set(refreshUpdatesAtom);
    }
});
